import React, { useEffect, useState } from "react";

const CALC_STORAGE_KEY = "matematron:exam-calculator:v1";

export default function ExamCalculator({ embedded = false }) {
  // --- STAN
  const [display, setDisplay] = useState(() => loadCalculatorState().display);
  const [acc, setAcc] = useState(() => loadCalculatorState().acc);
  const [op, setOp] = useState(() => loadCalculatorState().op); // "+", "-", "×", "÷"
  const [overwrite, setOverwrite] = useState(() => loadCalculatorState().overwrite);
  const [memory, setMemory] = useState(() => loadCalculatorState().memory);

const displayBg = "#D88C2B"; // zawsze pomarańczowy, niezależnie od motywu


  // Enter = "=" – tylko gdy komponent jest używany
  useEffect(() => {
    if (!embedded) return;
    function onKey(e) {
      if (e.key === "Enter") {
        e.preventDefault();
        equals();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [acc, op, display, embedded]);

  useEffect(() => {
    if (!embedded) return;
    persistCalculatorState({ display, acc, op, overwrite, memory });
  }, [embedded, display, acc, op, overwrite, memory]);

  // --- POMOCNICZE
  const fmt = (v) => {
    if (v == null || (typeof v === "number" && Number.isNaN(v))) return "BŁĄD";
    if (typeof v === "string") return v.replace(".", ",");
    const s = String(v);
    const normalized = s.includes(".")
      ? String(+(+v).toFixed(12)).replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1")
      : s;
    return normalized.replace(".", ",");
  };
  const opLabel = (o) => (o === "-" ? "−" : o);
  const isActiveOp = (label) => {
    if (!op) return false;
    if (label === "−") return op === "-";
    return op === label;
  };

  // --- AKCJE
  function inputDigit(d) {
    setDisplay((prev) => {
      if (overwrite || prev === "0") {
        setOverwrite(false);
        return d === "." ? "0." : d;
      }
      if (d === "." && prev.includes(".")) return prev;
      return prev + d;
    });
  }
  function allClear() { setDisplay("0"); setAcc(null); setOp(null); setOverwrite(true); }
  function clearEntry() { setDisplay("0"); setOverwrite(true); }
  function setOperator(next) {
    const current = parseFloat(display.replace(",", "."));
    if (acc === null) {
      setAcc(current);
    } else if (!overwrite && op) {
      const r = evalSafe(acc, current, op);
      const normalized = normalizeResult(r);
      setAcc(normalized);
      setDisplay(resultToDisplay(normalized));
    }
    setOp(next);
    setOverwrite(true);
  }
  function equals() {
    const current = parseFloat(display.replace(",", "."));
    if (acc !== null && op) {
      const r = evalSafe(acc, current, op);
      setDisplay(resultToDisplay(r));
      setAcc(null);
      setOp(null);
      setOverwrite(true);
    }
  }
  function negate() { setDisplay((p) => (p.startsWith("-") ? p.slice(1) : "-" + p)); }
  function percent() { const v = parseFloat(display); setDisplay(resultToDisplay(v / 100)); setOverwrite(true); }
  function square() { const v = parseFloat(display); setDisplay(resultToDisplay(v * v)); setOverwrite(true); }
  function sqrt() { const v = parseFloat(display); setDisplay(v < 0 ? "BŁĄD" : resultToDisplay(Math.sqrt(v))); setOverwrite(true); }
  function inv() { const v = parseFloat(display); setDisplay(v === 0 ? "BŁĄD" : resultToDisplay(1 / v)); setOverwrite(true); }

  // Pamięć
  function mPlus() { setMemory((m) => m + parseFloat(display || "0")); }
  function mMinus() { setMemory((m) => m - parseFloat(display || "0")); }
  function mRecall() { setDisplay(String(memory)); setOverwrite(true); }
  function mClear() { setMemory(0); }

  // layout klawiszy z wariantem (op/eq) -> większe
  const keys = [
    { l: "MC", f: mClear }, { l: "MR", f: mRecall }, { l: "M+", f: mPlus }, { l: "M−", f: mMinus },
    { l: "CE", f: clearEntry }, { l: "AC", f: allClear }, { l: "±", f: negate }, { l: "%", f: percent },
    { l: "7", f: () => inputDigit("7") }, { l: "8", f: () => inputDigit("8") }, { l: "9", f: () => inputDigit("9") }, { l: "÷", f: () => setOperator("÷"), variant: "op" },
    { l: "4", f: () => inputDigit("4") }, { l: "5", f: () => inputDigit("5") }, { l: "6", f: () => inputDigit("6") }, { l: "×", f: () => setOperator("×"), variant: "op" },
    { l: "1", f: () => inputDigit("1") }, { l: "2", f: () => inputDigit("2") }, { l: "3", f: () => inputDigit("3") }, { l: "−", f: () => setOperator("-"), variant: "op" },
    { l: "0", f: () => inputDigit("0") }, { l: ".", f: () => inputDigit(".") }, { l: "=", f: equals, variant: "eq" }, { l: "+", f: () => setOperator("+"), variant: "op" },
    { l: "x²", f: square }, { l: "√", f: sqrt }, { l: "1/x", f: inv },
  ];

  // --- RENDER
  if (!embedded) return null;

  return (
    <div
      className="exam-calculator-root"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        maxWidth: "unset",
        margin: 0,
        position: "relative",
        boxSizing: "border-box",
      }}
    >
      <style>{`
        .exam-calculator-root {
          --calc-pad: clamp(8px, 1.1vh, 12px);
          --calc-gap: clamp(5px, 0.85vh, 8px);
          --calc-key-size: clamp(48px, calc((100vh - 300px) / 7), 56px);
          --calc-display-pad-top: clamp(12px, 1.75vh, 18px);
          --calc-display-pad-side: clamp(10px, 1.25vh, 12px);
          --calc-display-min: clamp(46px, 6vh, 56px);
          --calc-display-font: clamp(22px, 3vh, 26px);
          --calc-key-font: clamp(13px, 1.65vh, 14px);
          --calc-op-font: clamp(15px, 2vh, 18px);
        }
        .exam-calculator-display-section {
          padding: var(--calc-pad) !important;
        }
        .exam-calculator-display {
          min-height: var(--calc-display-min) !important;
          padding: var(--calc-display-pad-top) var(--calc-display-pad-side) calc(var(--calc-display-pad-side) - 2px) var(--calc-display-pad-side) !important;
        }
        .exam-calculator-display-value {
          font-size: var(--calc-display-font) !important;
        }
        .exam-calculator-grid {
          gap: var(--calc-gap) !important;
          padding: var(--calc-pad) !important;
          grid-auto-rows: var(--calc-key-size);
        }
        .exam-calculator-key.btn-secondary,
        .exam-calculator-key.btn-secondary:hover,
        .exam-calculator-key.btn-secondary:focus,
        .exam-calculator-key.btn-secondary:focus-visible {
          box-shadow: none !important;
        }
        .exam-calculator-key.btn-secondary.is-active,
        .exam-calculator-key.btn-secondary.is-active:hover,
        .exam-calculator-key.btn-secondary.is-active:focus,
        .exam-calculator-key.btn-secondary.is-active:focus-visible {
          border-color: var(--color-text) !important;
          box-shadow: 0 0 0 2px var(--color-text) inset !important;
        }
        @media (max-height: 760px) {
          .exam-calculator-root {
            --calc-key-size: clamp(50px, calc((100vh - 310px) / 7), 56px);
            --calc-pad: clamp(8px, 1vh, 10px);
            --calc-gap: clamp(6px, 0.8vh, 8px);
            --calc-display-min: 50px;
            --calc-display-font: 24px;
          }
          .exam-calculator-help {
            padding-top: 6px !important;
            padding-bottom: 8px !important;
            font-size: 10px !important;
          }
        }
        @media (max-height: 660px) {
          .exam-calculator-root {
            --calc-key-size: clamp(42px, calc((100vh - 260px) / 7), 52px);
            --calc-pad: 7px;
            --calc-gap: 5px;
            --calc-display-min: 44px;
            --calc-display-font: 22px;
          }
        }
        @media (max-height: 560px) {
          .exam-calculator-root {
            --calc-key-size: 38px;
            --calc-pad: 6px;
            --calc-gap: 4px;
            --calc-display-min: 40px;
            --calc-display-font: 20px;
          }
          .exam-calculator-help {
            display: none;
          }
        }
      `}</style>
      {/* Display + status operacji */}
      <div className="exam-calculator-display-section" style={{ borderBottom: "1px solid var(--color-border, #222)", padding: 12 }}>
        <div
          className="exam-calculator-display"
          style={{
            position: "relative",
            background: displayBg,      // ⬅️ nieprzezroczysty kolor wg motywu
            borderRadius: 12,
            padding: "18px 12px 12px 12px",
            minHeight: 56,
          }}
        >
          {/* status: akumulator + operator */}
          {op && acc !== null && (
            <div
              style={{
                position: "absolute",
                left: 12,
                top: 8,
                fontSize: 14,
                opacity: 0.9,
              }}
            >
              {fmt(acc)}&nbsp;{opLabel(op)}
            </div>
          )}

          {/* wartość na wyświetlaczu */}
          <div
            className="exam-calculator-display-value"
            style={{
              fontSize: 26,
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {fmt(display)}
          </div>
        </div>

        <div style={{ textAlign: "right", fontSize: 11, opacity: 0.7, marginTop: 4 }}>
          M: {fmt(memory)}
        </div>
      </div>

      {/* Klawiatura */}
      <div
        className="exam-calculator-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 8,
          padding: 12,
        }}
      >
        {keys.map((k, i) => {
          const isOp = k.variant === "op";
          const isEq = k.variant === "eq";
          const active = isOp && isActiveOp(k.l);

          const baseStyle = {
            padding: isOp || isEq ? "clamp(8px, 1.4vh, 14px) 10px" : "clamp(6px, 1vh, 10px) 8px",
            borderRadius: 12,
            fontSize: isOp || isEq ? "var(--calc-op-font)" : "var(--calc-key-font)",
            lineHeight: 1.1,
            fontWeight: isOp || isEq ? 700 : 600,
            minHeight: "var(--calc-key-size)",
            height: "var(--calc-key-size)",
            boxSizing: "border-box",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background-color .2s ease, transform .2s ease, box-shadow .12s ease",
            outline: "none",
          };

          const activeStyle = active
            ? {
                borderColor: "var(--color-text)",
                boxShadow: "0 0 0 2px var(--color-text) inset",
              }
            : {};

          return (
            <button
              key={i}
              type="button"
              onClick={k.f}
              className={`btn-secondary exam-calculator-key${active ? " is-active" : ""}`}
              style={{ ...baseStyle, ...activeStyle }}
            >
              {k.l}
            </button>
          );
        })}
      </div>

      <div className="exam-calculator-help" style={{ marginTop: "auto", padding: 12, fontSize: 11, opacity: 0.7 }}>
        Podstawowe działania: +, −, ×, ÷, √, x², %, 1/x, pamięć (MC/MR/M±).
      </div>
    </div>
  );
}

function evalSafe(a, b, op) {
  switch (op) {
    case "+": return a + b;
    case "-": return a - b;
    case "×": return a * b;
    case "÷": return b === 0 ? NaN : a / b;
    default: return b;
  }
}

// JavaScript zapisuje liczby zmiennoprzecinkowe binarnie, dlatego np.
// 1.06 * 1.06 daje wewnętrznie 1.1236000000000002. Zachowujemy 15
// cyfr znaczących (bezpieczna precyzja Number) i usuwamy artefakt z ekranu.
function normalizeResult(value) {
  if (!Number.isFinite(value)) return value;
  return Number.parseFloat(value.toPrecision(15));
}

function resultToDisplay(value) {
  const normalized = normalizeResult(value);
  return Number.isFinite(normalized) ? String(normalized) : "BŁĄD";
}

function loadCalculatorState() {
  const fallback = {
    display: "0",
    acc: null,
    op: null,
    overwrite: true,
    memory: 0,
  };
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(CALC_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return {
      display:
        typeof parsed?.display === "string" && parsed.display.trim()
          ? parsed.display
          : "0",
      acc: Number.isFinite(parsed?.acc) ? Number(parsed.acc) : null,
      op: typeof parsed?.op === "string" ? parsed.op : null,
      overwrite:
        typeof parsed?.overwrite === "boolean" ? parsed.overwrite : true,
      memory: Number.isFinite(parsed?.memory) ? Number(parsed.memory) : 0,
    };
  } catch {
    return fallback;
  }
}

function persistCalculatorState(state) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      CALC_STORAGE_KEY,
      JSON.stringify({
        display: String(state?.display ?? "0"),
        acc: Number.isFinite(state?.acc) ? Number(state.acc) : null,
        op: typeof state?.op === "string" ? state.op : null,
        overwrite: Boolean(state?.overwrite),
        memory: Number.isFinite(state?.memory) ? Number(state.memory) : 0,
      })
    );
  } catch {
    // ignore
  }
}
